import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  CommentPopover,
  COMMENT_POPPER_WIDTH,
  MARGIN_FOR_COMMENT_POPPER_PLACEMENT_SWITCH,
} from '../CommentPopover/CommentPopover.component';
import { CommentTypeId, IComment } from '../Comments/Comments.types';
import {
  commentGroupData,
  commentImageData,
  commentImageDataWithPopperPosition,
  Coords,
  pageImageData,
  Point,
  PreviewAction,
  PreviewActionType,
  rotateAngleValue,
} from './Preview.types';
import useSessionUser from '../../hooks/useSessionUser';
import useLongClick from '../../hooks/useLongClick';
import {
  translatePagePositionToCanvasCoordinates,
  getPointRelativeToImage,
  getImageRotateOffsetForIcons,
  getTargetPageImage,
  makeImageLocked,
  makeImageMovable,
  getAbsoluteCoordinates,
  isRectFullyOverOnePageImage,
  COMMENT_ICON_SIZE,
  COMMENTZONE_FOR_EDIT_OBJECT_NAME,
  NEW_COMMENTZONE_OBJECT_NAME,
  COMMENTZONE_GROUP_OBJECT_NAME,
  COMMENTZONE_OBJECT_NAME,
  COMMENT_OBJECT_NAME,
} from './previewUtils';
import { updateComment } from '../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { fabric } from 'fabric';
import { OnPageCommentIcon } from '../Comments/icons';
import { OnPageCommentIconHighContrast } from '../Comments/icons';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { highContrastMode } from '../../utils/highContrastChecker';
import { svgToPng } from '../../utils/svg';
import { useCommentZone } from './useCommentZone';
import { useCanvasHandlers } from './useCanvasHandlers';
import { useAddingCommentZoneAction } from './useAddingCommentZoneAction';
import { PreviewType } from '../../pages/Solution/Solution.component';
import { Prompt } from '../Prompt/Prompt.component';

const getCurrentCommentIconScale = (zoom: number): number => {
  return COMMENT_ICON_SIZE / 33 / zoom;
};

interface CommentsOnPageManagerProps {
  canvas: fabric.Canvas;
  canvasZoom: number;
  comments: IComment[];
  orgzId?: string;
  pageImages: pageImageData[];

  commentsOnPageVisible: boolean;
  allCommentsOnPageVisible: boolean;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  canEditComment: boolean;
  rotateAngle: rotateAngleValue;
  examinerFunction: ExaminerFunction;
  isNewCommentActive: boolean;
  setIsNewCommentActive: (value: boolean) => void;
  updateIsMouseScrollDisabled: (value: boolean) => void;

  actionsToTrigger: PreviewAction[];
  onActionTrigger: (action: PreviewAction) => void;
  onCommentChanged: (comment: IComment | null) => void;

  getPopoverCoord: (left: number, top: number, zoom: number, offset: Coords) => Point;
  getOffsetForPopovers: () => Coords;
  getCanvasPoint: (clientX: number, clientY: number) => Point;
  previewType: PreviewType;
}

const CommentsOnPageManager: React.FC<CommentsOnPageManagerProps> = (props) => {
  const { getPopoverCoord, getCanvasPoint, pageImages, isNewCommentActive } = props;

  const [shouldDisplayAllComments, setShouldDisplayAllComments] = useState(props.allCommentsOnPageVisible);
  const lastSouldDisplayAllComments = useRef(props.allCommentsOnPageVisible);

  const { user } = useSessionUser();
  const { openAlert, isCommentPopoverOpen } = useLayoutContext();
  const { beginLongClick, cancelLongClick } = useLongClick();

  const commentImages = useRef<commentImageData[]>([]);
  const commentIdToShowZoneAfterRecreation = useRef<string | null>(null);
  const [commentGroups, setCommentGroups] = useState<commentGroupData[]>([]);

  const [commentsWithPopperCoords, setCommentsWithPopperCoords] = useState<commentImageDataWithPopperPosition[]>([]);
  const [closedCommentsIds, setClosedCommentsIds] = useState<string[]>([]);
  const [activeOnPageComment, setActiveOnPageComment] = useState<commentImageDataWithPopperPosition | undefined>(
    undefined,
  );
  const [commentsToDisplay, setCommentsToDisplay] = useState<IComment[] | undefined>(props.comments);
  const [newComment, setNewComment] = useState<IComment | undefined>(undefined);
  const [editedCommentGroup, setEditedCommentGroup] = useState<commentGroupData | undefined>(undefined);
  const [isCommentInMove, setIsCommentInMove] = useState<boolean>(false);
  const [commentOutsideRGZPromptVisible, setCommentOutsideRGZPromptVisible] = useState<boolean>(false);
  const [refreshComments, setRefreshComments] = useState<boolean>(false);
  const [showNewCommentPopover, setShowNewCommentPopover] = useState(false);

  const commentCursor = useRef<string>('');
  const isFinishingEditZone = useRef(false);
  const lastEditedCommentId = useRef<string | null>(null);
  const commentGroupsRef = useRef<commentGroupData[]>([]);
  const editedCommentGroupRef = useRef<commentGroupData | undefined>(undefined);

  const clearEditedCommentZone = () => {
    props.canvas.getObjects().forEach((item) => {
      if (item.name && [COMMENTZONE_FOR_EDIT_OBJECT_NAME, NEW_COMMENTZONE_OBJECT_NAME].includes(item.name)) {
        props.canvas.remove(item);
        props.canvas.renderAll();
        setRefreshComments(true);
        if (item.name === COMMENTZONE_FOR_EDIT_OBJECT_NAME) {
          setEditedCommentGroup(undefined);
        }
      }
    });
  };

  useEffect(() => {
    setShouldDisplayAllComments(props.allCommentsOnPageVisible);
  }, [props.allCommentsOnPageVisible]);

  useEffect(() => {
    lastSouldDisplayAllComments.current = shouldDisplayAllComments;
  }, [shouldDisplayAllComments]);

  useEffect(() => {
    commentGroupsRef.current = commentGroups;
  }, [commentGroups]);

  useEffect(() => {
    editedCommentGroupRef.current = editedCommentGroup;
  }, [editedCommentGroup]);

  const getCommentPointRotated = useCallback(
    (x: number, y: number) => {
      const { leftOffset, topOffset } = getImageRotateOffsetForIcons(props.rotateAngle, x, y);
      return {
        x: leftOffset,
        y: topOffset,
      };
    },
    [props.rotateAngle],
  );

  const updateCommentPopperPositionForAllComments = useCallback(
    (list: commentImageData[], zoom: number, offset: Coords) => {
      if (shouldDisplayAllComments) {
        const commentsToOpen: commentImageDataWithPopperPosition[] = list.map((item: commentImageData) => {
          const iconCoords = getAbsoluteCoordinates(item.image);
          return {
            image: item.image,
            comment: item.comment,
            ...getPopoverCoord(iconCoords.left, iconCoords.top - COMMENT_ICON_SIZE / 2, zoom, offset),
          };
        });
        setCommentsWithPopperCoords(commentsToOpen);
      }
    },
    [getPopoverCoord, shouldDisplayAllComments],
  );

  const updateCommentsOrder = (commentId: string): void => {
    const newList = [...commentImages.current];
    const selectedIndex = newList.findIndex((item) => item.comment.commentId === commentId);
    if (selectedIndex === -1) return;

    const [removedItem] = newList.splice(selectedIndex, 1);
    newList.splice(newList.length, 0, removedItem);
    commentImages.current = newList;
    const zoom = props.canvas.getZoom();
    const offset = props.getOffsetForPopovers();
    updateCommentPopperPositionForAllComments(newList, zoom, offset);
  };

  const openCommentPopover = (image: fabric.Object | fabric.Group, comment: IComment, skipCreateGroup?: boolean) => {
    props.canvas.getObjects().forEach((item) => {
      if (item.name === COMMENTZONE_FOR_EDIT_OBJECT_NAME) {
        props.canvas.remove(item);
      }
    });
    props.canvas.renderAll();

    setEditedCommentGroup(undefined);
    clearEditedCommentZone();
    if (lastSouldDisplayAllComments.current && !props.isNewCommentActive && !editedCommentGroupRef.current) {
      setClosedCommentsIds((prevState) => prevState.filter((id) => id !== comment.commentId));
      updateCommentsOrder(comment.commentId);
    } else {
      const zoom = props.canvas.getZoom();
      const offset = props.getOffsetForPopovers();
      const imageCoords =
        image.name === COMMENTZONE_GROUP_OBJECT_NAME && image instanceof fabric.Group && image._objects?.[0]
          ? getAbsoluteCoordinates(image._objects?.[0])
          : getAbsoluteCoordinates(image);

      const activeComment = {
        image,
        comment,
        ...getPopoverCoord(imageCoords.left, (imageCoords.top ?? 0) - COMMENT_ICON_SIZE / 2, zoom, offset),
      };

      setActiveOnPageComment(activeComment);

      // Only create group if it doesn't already exist for this comment
      if (
        !skipCreateGroup &&
        activeComment.comment.x2 !== null &&
        activeComment.comment.y2 !== null &&
        !commentGroupsRef.current.some((g) => g.commentId === comment.commentId)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        createGroup(activeComment);
      }
    }
  };

  const getZoneDimensionsForComment = (
    comment: IComment,
    left: number,
    top: number,
  ): { left: number; top: number; width: number; height: number } => {
    const width = comment.x1 && comment.x2 ? Math.abs(comment.x1 - comment.x2) : 0;
    const height = comment.y1 && comment.y2 ? Math.abs(comment.y1 - comment.y2) : 0;
    const rotatedDimensions = getCommentPointRotated(width, height);

    return {
      left,
      top,
      width: rotatedDimensions.x,
      height: rotatedDimensions.y,
    };
  };

  const createZone = (comment: IComment, left: number, top: number): fabric.Rect => {
    const zoneDimensions = getZoneDimensionsForComment(comment, left, top);
    return new fabric.Rect({
      ...zoneDimensions,
      fill: 'rgba(255, 230, 0, 0.2)',
      stroke: 'rgba(255, 230, 0, 0.35)',
      evented: false,
      visible: true,
      selectable: false,
      name: COMMENTZONE_OBJECT_NAME,
    });
  };

  const isCommentIconAndZoneOnPage = (
    iconLeft: number,
    iconTop: number,
    comment: IComment,
    findInWholePageInsteadOfDisplayedImageZoneForZone: boolean,
  ) => {
    const zoneDimensions = getZoneDimensionsForComment(comment, iconLeft, iconTop);
    return !!(
      isRectFullyOverOnePageImage(
        props.pageImages,
        iconLeft,
        iconTop,
        COMMENT_ICON_SIZE,
        -COMMENT_ICON_SIZE /* minus because of comment origin point is left-bottom */,
        false,
      ) &&
      isRectFullyOverOnePageImage(
        props.pageImages,
        zoneDimensions.left,
        zoneDimensions.top,
        zoneDimensions.width,
        zoneDimensions.height,
        findInWholePageInsteadOfDisplayedImageZoneForZone,
      )
    );
  };

  const updateMovedComment = (image: fabric.Object, comment: IComment, isZone?: boolean) => {
    const { left, top } = image;
    if (left === undefined || top === undefined) return;

    const targetImageData = getTargetPageImage(props.pageImages, left, top, 0, false);

    if (!targetImageData || !isCommentIconAndZoneOnPage(left, top, comment, true)) {
      setCommentOutsideRGZPromptVisible(true);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      recreateAllComments();
      return;
    }

    const commentCoords = getPointRelativeToImage(left, top, targetImageData);
    const hasMoved =
      commentCoords.x !== comment.x1 ||
      commentCoords.y !== comment.y1 ||
      targetImageData.pageNumber !== comment.pageNumber;

    if (!hasMoved) return;

    const yOffset = isZone ? 50 : 0;
    const x1 = commentCoords.x;
    const y1 = commentCoords.y + yOffset;
    const x2 = comment.x1 && comment.x2 ? commentCoords.x + Math.abs(comment.x2 - comment.x1) : null;
    const y2 = comment.y1 && comment.y2 ? commentCoords.y + yOffset + Math.abs(comment.y2 - comment.y1) : null;

    setIsCommentInMove(true);

    updateComment(comment.commentId, comment.content, x1, y1, x2, y2, targetImageData.pageNumber).then(
      (response: AxiosResponse<boolean>) => {
        if (!response.data) return openAlert('save_error');

        setCommentsToDisplay((prev) =>
          prev?.map((item) =>
            item.commentId === comment.commentId
              ? {
                  ...item,
                  x1,
                  y1,
                  x2,
                  y2,
                  pageNumber: targetImageData.pageNumber,
                }
              : item,
          ),
        );
        props.onCommentChanged(comment);
        setIsCommentInMove(false);
      },
      () => openAlert('save_error'),
    );
  };

  const clearCommentImages = (): void => {
    commentImages.current.forEach((obj: commentImageData) => {
      props.canvas.remove(obj.image);
    });
    commentGroups.forEach((obj: commentGroupData) => {
      props.canvas.remove(obj.group);
    });
    commentImages.current = [];
    setCommentGroups([]);

    props.canvas.getObjects().forEach((item) => {
      if (
        item.name &&
        [COMMENT_OBJECT_NAME, COMMENTZONE_OBJECT_NAME, COMMENTZONE_GROUP_OBJECT_NAME].includes(item.name)
      ) {
        props.canvas.remove(item);
      }
    });
  };

  const commentMouseHandlers = (
    target: fabric.Object | fabric.Group,
    comment: IComment,
    isAuthor: boolean,
    isZone?: boolean,
  ) => {
    let wasMoved = false;

    target.on('mousedown', (event: fabric.IEvent) => {
      if (event.e && (event.e as MouseEvent).button === 2) {
        (event.e as MouseEvent).preventDefault();
        (event.e as MouseEvent).stopPropagation();
        return;
      }
      wasMoved = false;
      if (isAuthor && props.canEditComment) {
        event.e.preventDefault();
        event.e.stopPropagation();
        props.updateIsMouseScrollDisabled(true);
        beginLongClick(() => {
          makeImageMovable(target);
          target.set({ borderColor: '#3560FF', borderOpacityWhenMoving: 1 });
          props.canvas.defaultCursor = 'move';
          props.canvas.hoverCursor = 'move';
          props.canvas.setCursor('move');
          props.canvas.renderAll();
        });
      }
    });

    target.on('mousemove', () => {
      wasMoved = true;
      cancelLongClick();
    });

    target.on('mouseup', (event: fabric.IEvent) => {
      if (event.e && (event.e as MouseEvent).button === 2) {
        (event.e as MouseEvent).preventDefault();
        (event.e as MouseEvent).stopPropagation();
        return;
      }
      updateCommentsOrder(comment.commentId);
      cancelLongClick();
      if (!wasMoved) {
        target.set({ borderColor: 'transparent', borderOpacityWhenMoving: 0 });
        props.canvas.renderAll();
      }
      if (target.lockMovementX) {
        if (!editedCommentGroup || editedCommentGroup.commentId !== comment.commentId) {
          openCommentPopover(target, comment);
        }
      } else {
        updateMovedComment(target, comment, isZone);
      }
      makeImageLocked(target);
      props.canvas.defaultCursor = 'pointer';
      props.canvas.hoverCursor = 'pointer';
      props.canvas.setCursor('pointer');
      props.updateIsMouseScrollDisabled(false);
    });
  };

  const addCommentGroup = (image: fabric.Object, comment: IComment) => {
    const { left, top } = image;

    if (!left || !top) return;

    const zone = createZone(comment, left, top);
    const isZone = !!(zone.width && zone.height);

    const zoneLeft = zone.left ?? -1;
    const zoneTop = zone.top ?? -1;
    if (zoneLeft < 0 || zoneTop < 0) return;
    if (!isZone) return;

    const group = new fabric.Group([image, zone]);
    group.set({
      selectable: true,
      hasControls: false,
      hasBorders: false,
      name: COMMENTZONE_GROUP_OBJECT_NAME,
    });

    makeImageLocked(group);

    setCommentGroups((prev) => [...prev, { commentId: comment.commentId, group }]);

    const currentUserIsAuthor = comment.examinerId === user.profile.sub;

    commentMouseHandlers(group, comment, currentUserIsAuthor, isZone);
    props.canvas.add(group);
    if (isZone) {
      props.canvas.remove(image);
    }
    props.canvas.renderAll();
    group.bringToFront();
  };

  const removeCommentGroup = () => {
    commentGroups.forEach((e) => {
      props.canvas.remove(e.group);
    });
    setCommentGroups([]);
    setEditedCommentGroup(undefined);
    props.canvas.renderAll();
  };

  const activeObject = props.canvas.getActiveObject();

  function createGroup(comment: commentImageDataWithPopperPosition) {
    addCommentGroup(comment.image, comment.comment);
  }

  function removeGroup() {
    const isEditedZoneActive = activeObject?.name === COMMENTZONE_FOR_EDIT_OBJECT_NAME;
    const hasExistingGroups = commentGroups.length > 0;
    const isNotDisplayingAll = !shouldDisplayAllComments;
    const isNotEditingOrOutsideZone = !editedCommentGroup || !isEditedZoneActive;

    if (hasExistingGroups && (isNotDisplayingAll || isNewCommentActive) && isNotEditingOrOutsideZone) {
      removeCommentGroup();
    }
  }

  useEffect(() => {
    if (activeObject) return;
    clearEditedCommentZone();
  }, [activeObject, props.canvas]);

  useEffect(() => {
    isCommentPopoverOpen.current = !!activeOnPageComment;
  }, [activeOnPageComment, isCommentPopoverOpen]);

  useEffect(() => {
    if (shouldDisplayAllComments) {
      commentImages.current.forEach((e) => {
        // Skip if just edited
        if (lastEditedCommentId.current === e.comment.commentId) return;
        // Skip if editing or if a non-editable rect exists for this comment
        const rectExists = props.canvas
          .getObjects()
          .some(
            (obj) =>
              obj.name === COMMENTZONE_FOR_EDIT_OBJECT_NAME &&
              (obj as any).left === e.image.left &&
              (obj as any).top === e.image.top,
          );
        if (
          (editedCommentGroup && editedCommentGroup.commentId === e.comment.commentId) ||
          rectExists ||
          (commentGroups.length && commentGroups.some((item) => item.commentId === e.comment.commentId))
        )
          return;
        addCommentGroup(e.image, e.comment);
      });
    } else removeCommentGroup();
  }, [shouldDisplayAllComments, editedCommentGroup]);

  const rotateCommentImages = (pageImageCoords: Coords) => {
    const needsRecreatingZonesAndPopovers = shouldDisplayAllComments;
    if (!props.isNewCommentActive) {
      setShouldDisplayAllComments(false);
    }
    try {
      commentImages.current.forEach((commentImage: commentImageData) => {
        if (commentImage.comment.x1 !== null && commentImage.comment.y1 !== null) {
          const { leftOffset, topOffset } = getImageRotateOffsetForIcons(
            props.rotateAngle,
            commentImage.comment.x1,
            commentImage.comment.y1,
          );
          commentImage.image.set({
            left: pageImageCoords.left + leftOffset,
            top: pageImageCoords.top + topOffset,
          });
        }
      });
    } finally {
      if (needsRecreatingZonesAndPopovers) {
        setTimeout(() => {
          setShouldDisplayAllComments(true);
        }, 0);
      }
    }
  };

  const updateCommentPopperPosition = () => {
    const zoom = props.canvas.getZoom();
    const offset = props.getOffsetForPopovers();

    if (shouldDisplayAllComments) {
      updateCommentPopperPositionForAllComments(
        commentImages.current,
        props.canvas.getZoom(),
        props.getOffsetForPopovers(),
      );
    }
    if (activeOnPageComment || commentsWithPopperCoords.length) {
      if (activeOnPageComment) {
        const selectedImage = commentImages.current.filter(
          (e) => e.comment.commentId === activeOnPageComment.comment.commentId,
        )[0];
        const imageCoords = getAbsoluteCoordinates(selectedImage.image);

        setActiveOnPageComment((prevState: commentImageDataWithPopperPosition | undefined) => {
          if (!prevState) {
            return prevState;
          } else
            return {
              ...prevState,
              ...getPopoverCoord(imageCoords.left, imageCoords.top - COMMENT_ICON_SIZE / 2, zoom, offset),
            };
        });
      }

      setCommentsWithPopperCoords((prevState) => {
        return prevState.map((item: commentImageDataWithPopperPosition) => {
          const imageCoords = getAbsoluteCoordinates(item.image);
          return {
            ...item,
            ...getPopoverCoord(imageCoords.left ?? 0, imageCoords.top - COMMENT_ICON_SIZE / 2, zoom, offset),
          };
        });
      });
    }
  };

  useEffect(() => {
    if (!props.commentsOnPageVisible) {
      removeCommentGroup();
      clearCommentImages();
      lastSouldDisplayAllComments.current = false;
    }
  }, [props.commentsOnPageVisible]);

  const handleCancelComment = () => {
    setShowNewCommentPopover(false);
    setActiveOnPageComment(undefined);
    setNewComment(undefined);
    removeGroup();
    props.setIsNewCommentActive(false);

    if (!shouldDisplayAllComments) {
      clearEditedCommentZone();
      setEditedCommentGroup(undefined);
    }

    if (newComment) {
      setCommentsToDisplay((prevState) => prevState?.filter((item: IComment) => item !== newComment));
      const newCommentData = commentImages.current.find((obj: commentImageData) => obj.comment === newComment);
      if (newCommentData) {
        props.canvas.remove(newCommentData.image);
        commentImages.current = commentImages.current.filter((obj) => obj.comment !== newComment);
      }
      const newCommentGroup = commentGroups.find((obj) => obj.commentId === newComment.commentId);
      if (newCommentGroup) {
        props.canvas.remove(newCommentGroup.group);
        setCommentGroups((prev) => prev.filter((obj) => obj.commentId !== newComment.commentId));
      }
      setNewComment(undefined);
    }
    props.canvas.renderAll();

    if (lastSouldDisplayAllComments.current) {
      setShouldDisplayAllComments(true);
    }
  };

  useEffect(() => {
    if (!props.commentsOnPageVisible) {
      clearCommentImages();
      lastSouldDisplayAllComments.current = false;
    }
  }, [props.commentsOnPageVisible]);

  const translateZoneCoordinatesToCommentData = (x1: number, y1: number, x2?: number, y2?: number) => {
    const targetImageData: pageImageData | undefined = getTargetPageImage(props.pageImages, x1, y1);
    if (
      targetImageData &&
      targetImageData.image &&
      targetImageData.image.left !== undefined &&
      targetImageData.image.top !== undefined
    ) {
      const coords1 = getPointRelativeToImage(x1, y1, targetImageData);
      const coords2 = x2 && y2 ? getPointRelativeToImage(x2, y2, targetImageData) : undefined;
      return {
        x1: coords1.x,
        y1: coords1.y,
        x2: coords2 ? coords2.x : null,
        y2: coords2 ? coords2.y : null,
        pageNumber: targetImageData.pageNumber,
      };
    }
    return null;
  };

  const renderNewCommentIconAndOpenPopover = (iconCoordinates: Coords, newComment: IComment) => {
    fabric.loadSVGFromString(
      highContrastMode ? OnPageCommentIconHighContrast : OnPageCommentIcon,
      function (objects, options) {
        const source = fabric.util.groupSVGElements(objects, options);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        addCommentIcon(source, iconCoordinates.left, iconCoordinates.top, newComment);

        setTimeout(() => {
          const imageData = commentImages.current.find((obj) => obj.comment === newComment);
          if (imageData) {
            openCommentPopover(imageData.image, newComment);
          }
        }, 0);
      },
    );
  };

  const handleAddNewComment = (x1: number, y1: number, x2?: number, y2?: number) => {
    if (activeOnPageComment) {
      handleCancelComment();
    }

    const commentData = translateZoneCoordinatesToCommentData(x1, y1, x2, y2);
    if (!commentData) {
      return;
    }
    const newComment = {
      commentId: 'new_comment_id',
      examinerId: user.profile.sub,
      examinerFirstName: user.profile.given_name ?? '',
      examinerLastName: user.profile.family_name ?? '',
      examinerFunction: props.examinerFunction,
      commentTypeId: CommentTypeId.ONIMAGE,
      history: [],
      content: '',
      x1: commentData.x1,
      y1: commentData.y1,
      x2: commentData.x2,
      y2: commentData.y2,
      pageNumber: commentData.pageNumber,
    };

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const iconCoordinates = getIconPointForComment(newComment);
    if (!iconCoordinates) {
      return;
    }

    if (!isCommentIconAndZoneOnPage(iconCoordinates.left, iconCoordinates.top, newComment, false)) {
      return;
    }

    setNewComment(newComment);
    setCommentsToDisplay((prevState) => {
      return prevState ? [...prevState, newComment] : [newComment];
    });
    setShowNewCommentPopover(true);
    props.setIsNewCommentActive(false);

    renderNewCommentIconAndOpenPopover(iconCoordinates, newComment);
  };

  const setCommentAssets = (image: fabric.Object, comment: IComment) => {
    commentImages.current = [
      ...commentImages.current,
      {
        image,
        comment,
      },
    ];
  };

  const addCommentIcon = (source: fabric.Object, left: number, top: number, comment: IComment) => {
    source.clone((image: fabric.Object) => {
      image.set({
        left,
        top,
        originX: 'left',
        originY: 'bottom',
        hasControls: false,
        borderColor: '#3560FF',
        borderOpacityWhenMoving: 1,
        selectable: true,
      });
      image.scale(getCurrentCommentIconScale(props.canvas.getZoom()));

      image.name = COMMENT_OBJECT_NAME;
      makeImageLocked(image);
      setCommentAssets(image, comment);

      const currentUserIsAuthor = comment.examinerId === user.profile.sub;

      commentMouseHandlers(image, comment, currentUserIsAuthor);

      if (newComment) {
        clearEditedCommentZone();
      }

      props.canvas.add(image);
      props.canvas.renderAll();
      image.bringToFront();

      if (comment.commentId === commentIdToShowZoneAfterRecreation.current && !newComment) {
        const groupData = commentGroups.find((g) => g.commentId === comment.commentId);
        if (groupData) {
          props.canvas.setActiveObject(groupData.group);
          openCommentPopover(groupData.group, comment);
        } else {
          props.canvas.setActiveObject(image);
          openCommentPopover(image, comment);
        }
        commentIdToShowZoneAfterRecreation.current = null;
        isFinishingEditZone.current = false;
      }
    });
  };

  useEffect(() => {
    if (commentIdToShowZoneAfterRecreation.current) {
      const groupData = commentGroups.find((g) => g.commentId === commentIdToShowZoneAfterRecreation.current);
      if (groupData) {
        props.canvas.setActiveObject(groupData.group);
        const comment = (commentsToDisplay || props.comments).find((c) => c.commentId === groupData.commentId);
        if (comment) {
          openCommentPopover(groupData.group, comment);
        }
        commentIdToShowZoneAfterRecreation.current = null;
      }
    }
  }, [commentGroups, commentsToDisplay, props.comments]);

  const handleCloseOneOfOpenedComment = (commentId: string) => {
    setClosedCommentsIds((prevState) => [...prevState, commentId]);
  };

  const handleRemoveComment = (comment: IComment) => {
    const deletedCommentData = commentImages.current.find(
      (obj: commentImageData) => obj.comment.commentId === comment.commentId,
    );
    if (deletedCommentData) {
      props.canvas.remove(deletedCommentData.image);
    }
    setActiveOnPageComment(undefined);
    removeGroup();
    setCommentsToDisplay((prevState) => prevState?.filter((item: IComment) => item.commentId !== comment.commentId));
    props.onCommentChanged?.(comment);
  };

  const updateCommentZone = (
    comment: IComment,
    left: number,
    top: number,
    width?: number,
    height?: number,
    onFinish?: (success: boolean) => void,
  ) => {
    const commentZone = {
      x1: left,
      y1: top,
      x2: width ? left + width : undefined,
      y2: height ? top + height : undefined,
    };

    const commentData = translateZoneCoordinatesToCommentData(
      commentZone.x1,
      commentZone.y1,
      commentZone.x2,
      commentZone.y2,
    );
    if (!commentData) {
      onFinish?.(false);
      return;
    }

    const tmpComment = { ...comment, ...commentData };
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const iconCoordinates = getIconPointForComment(tmpComment);
    if (!iconCoordinates) {
      onFinish?.(false);
      return;
    }

    const isOnPage = isCommentIconAndZoneOnPage(iconCoordinates.left, iconCoordinates.top, tmpComment, false);
    if (!isOnPage) {
      onFinish?.(false);
      return;
    }

    updateComment(
      comment.commentId,
      comment.content,
      commentData.x1,
      commentData.y1,
      commentData.x2,
      commentData.y2,
      commentData.pageNumber,
    )
      .then(
        (response: AxiosResponse<boolean>) => {
          if (!response.data) return openAlert('save_error');

          setCommentsToDisplay((prev) =>
            prev
              ? prev.map((item) =>
                  item.commentId === comment.commentId
                    ? {
                        ...item,
                        x1: commentData.x1,
                        y1: commentData.y1,
                        x2: commentData.x2,
                        y2: commentData.y2,
                        pageNumber: commentData.pageNumber,
                      }
                    : item,
                )
              : prev,
          );

          isFinishingEditZone.current = true;
          commentIdToShowZoneAfterRecreation.current = comment.commentId;

          props.onCommentChanged(comment);
          onFinish?.(true);
        },
        () => {
          onFinish?.(false);
          openAlert('save_error');
        },
      )
      .catch(() => {
        onFinish?.(false);
      });
  };

  const handleRemoveZone = (comment: IComment) => {
    setActiveOnPageComment(undefined);
    removeGroup();
    props.canvas.renderAll();
    props.onCommentChanged?.(comment);
  };

  const handleEditZone = (comment: IComment) => {
    const activeOnPageCommentIdToRecreate = activeOnPageComment?.comment.commentId;
    setActiveOnPageComment(undefined);

    const editedGroupData = commentGroups.find((obj) => obj.commentId === comment.commentId);
    if (!editedGroupData) return;

    const commentIcon = editedGroupData.group._objects[0];
    editedGroupData.group.ungroupOnCanvas(); //required to restore original icon position
    props.canvas.add(commentIcon);
    props.canvas.remove(editedGroupData.group);
    props.canvas.renderAll();

    setEditedCommentGroup(editedGroupData);
    setCommentGroups((prev) => prev.filter((obj) => obj.commentId !== comment.commentId));

    const coords = translatePagePositionToCanvasCoordinates(
      props.pageImages,
      Math.min(comment.x1 ?? 0, comment.x2 ?? 0),
      Math.min(comment.y1 ?? 0, comment.y2 ?? 0),
      comment.pageNumber,
    );

    const zoneLeft = coords?.left || 0;
    const zoneTop = coords?.top || 0;
    const zoneWidth = comment.x1 && comment.x2 ? Math.abs(comment.x1 - comment.x2) : undefined;
    const zoneHeight = comment.y1 && comment.y2 ? Math.abs(comment.y1 - comment.y2) : undefined;

    const zoneEditRect = new fabric.Rect({
      left: zoneLeft,
      top: zoneTop,
      width: zoneWidth,
      height: zoneHeight,
      fill: 'rgba(255, 230, 0, 0.2)',
      stroke: 'rgba(255, 230, 0, 0.35)',
      visible: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
      lockRotation: true,
      lockMovementX: true,
      lockMovementY: true,
      name: COMMENTZONE_FOR_EDIT_OBJECT_NAME,
    });
    zoneEditRect.setControlVisible('mtr', false);

    props.canvas.add(zoneEditRect);
    props.canvas.setActiveObject(zoneEditRect);
    props.canvas.renderAll();
    const initalScaleX = zoneEditRect.scaleX;
    const initalScaleY = zoneEditRect.scaleY;

    zoneEditRect.on('mousedown', () => {
      props.updateIsMouseScrollDisabled(true);
    });

    zoneEditRect.on('mouseup', () => {
      props.updateIsMouseScrollDisabled(false);
      const width = zoneEditRect.getScaledWidth();
      const height = zoneEditRect.getScaledHeight();
      updateCommentZone(
        comment,
        zoneEditRect.left || zoneLeft,
        zoneEditRect.top || zoneTop,
        width,
        height,
        (success) => {
          if (success) {
            clearEditedCommentZone();
            setEditedCommentGroup(undefined);
            commentIdToShowZoneAfterRecreation.current = comment.commentId;

            if (lastSouldDisplayAllComments.current) {
              setShouldDisplayAllComments(true);
            }
          } else {
            clearEditedCommentZone();
            setEditedCommentGroup(undefined);

            // Restore the popover if needed
            if (activeOnPageCommentIdToRecreate) {
              const imageData = commentImages.current.find(
                (obj) => obj.comment.commentId === activeOnPageCommentIdToRecreate,
              );
              if (imageData) {
                const zoom = props.canvas.getZoom();
                const offset = props.getOffsetForPopovers();
                const imageCoords = getAbsoluteCoordinates(imageData.image);
                setActiveOnPageComment({
                  image: imageData.image,
                  comment: imageData.comment,
                  ...props.getPopoverCoord(imageCoords.left, imageCoords.top - COMMENT_ICON_SIZE / 2, zoom, offset),
                });
              } else {
                setActiveOnPageComment(undefined);
                clearEditedCommentZone();
                setEditedCommentGroup(undefined);
              }
            } else {
              setActiveOnPageComment(undefined);
              clearEditedCommentZone();
              setEditedCommentGroup(undefined);
            }

            // Only restore the non-editable zone if all comments are NOT visible
            if (
              !shouldDisplayAllComments &&
              typeof zoneLeft === 'number' &&
              typeof zoneTop === 'number' &&
              typeof zoneWidth === 'number' &&
              typeof zoneHeight === 'number' &&
              zoneWidth > 0 &&
              zoneHeight > 0
            ) {
              const restoredRect = new fabric.Rect({
                left: zoneLeft,
                top: zoneTop,
                width: zoneWidth,
                height: zoneHeight,
                fill: 'rgba(255, 230, 0, 0.2)',
                stroke: 'rgba(255, 230, 0, 0.35)',
                visible: true,
                evented: false,
                selectable: false,
                hasControls: false,
                hasBorders: false,
                lockRotation: true,
                lockMovementX: true,
                lockMovementY: true,
                name: COMMENTZONE_FOR_EDIT_OBJECT_NAME,
                scaleX: initalScaleX,
                scaleY: initalScaleY,
              });
              restoredRect.setControlVisible('mtr', false);
              props.canvas.add(restoredRect);
              props.canvas.renderAll();
            }
          }
        },
      );
    });
  };

  const handleAppendZone = (comment: IComment) => {
    const zoneLeft = Math.min(comment.x1 ?? 0, comment.x2 ?? 0);
    const zoneTop = Math.min(comment.y1 ?? 0, comment.y2 ?? 0);
    const zoneWidth = comment.x1 && comment.x2 ? Math.abs(comment.x1 - comment.x2) : undefined;
    const zoneHeight = comment.y1 && comment.y2 ? Math.abs(comment.y1 - comment.y2) : undefined;
    updateCommentZone(comment, zoneLeft, zoneTop, zoneWidth, zoneHeight, (success) => {
      if (success) {
        commentIdToShowZoneAfterRecreation.current = comment.commentId;
      } else {
        props.onCommentChanged(comment);
      }
    });
  };

  const { startAddingZone } = useAddingCommentZoneAction({
    canvas: props.canvas,
    getCanvasPoint: props.getCanvasPoint,
    onFinishAction: (comment: IComment) => {
      if (comment.x2 && comment.y2) {
        handleAppendZone(comment);
      }
      props.updateIsMouseScrollDisabled(false);
    },
    pageImages: props.pageImages,
    setCommentsToDisplay,
  });

  const handleAddZone = (comment: IComment) => {
    const iconData = commentImages.current.find((obj) => obj.comment.commentId === comment.commentId);
    if (!iconData) {
      return;
    }
    props.updateIsMouseScrollDisabled(true);
    startAddingZone(iconData);
    setActiveOnPageComment(undefined);
  };

  const handleSaveComment = (comment: IComment, content: string) => {
    setShowNewCommentPopover(false);
    props.setIsNewCommentActive(false);
    props.onCommentChanged && props.onCommentChanged(comment);

    if (newComment) {
      setNewComment(undefined);
      setActiveOnPageComment(undefined);
      removeGroup();
      props.canvas.renderAll();

      if (lastSouldDisplayAllComments.current) {
        setShouldDisplayAllComments(true);
      }
    }
    if (!newComment && props.orgzId && activeOnPageComment) {
      setActiveOnPageComment((prevState: commentImageDataWithPopperPosition | undefined) => {
        if (!prevState) {
          return prevState;
        } else
          return {
            ...prevState,
            comment: { ...comment, content },
          };
      });
    }
  };

  function getIconPointForComment(comment: IComment): Coords | null {
    if (comment.x1 === null || comment.y1 == null) {
      return null;
    }

    const rotatedPoint = getCommentPointRotated(comment.x1, comment.y1);
    const commentCoordinates = translatePagePositionToCanvasCoordinates(
      props.pageImages,
      rotatedPoint.x,
      rotatedPoint.y,
      comment.pageNumber,
    );
    if (!commentCoordinates) {
      return null;
    }
    return commentCoordinates;
  }

  const getCommentPopperPlacement = (commentData: commentImageDataWithPopperPosition): 'left' | 'right' => {
    return props.canvasContainerRef?.current &&
      commentData.x + COMMENT_POPPER_WIDTH >
        props.canvasContainerRef.current.clientWidth - MARGIN_FOR_COMMENT_POPPER_PLACEMENT_SWITCH
      ? 'left'
      : 'right';
  };

  // defined in old style function name(){} to let it be used before definition)
  function recreateAllComments() {
    if (
      !props.orgzId ||
      !props.pageImages.length ||
      commentGroups.length ||
      activeOnPageComment ||
      isCommentInMove ||
      editedCommentGroup
    ) {
      return;
    }

    const needsRecreatingZonesAndPopovers = lastSouldDisplayAllComments.current;
    try {
      clearCommentImages();
      if (!commentsToDisplay?.length) {
        return;
      }
      if (props.commentsOnPageVisible) {
        fabric.loadSVGFromString(
          highContrastMode ? OnPageCommentIconHighContrast : OnPageCommentIcon,
          function (objects, options) {
            const source = fabric.util.groupSVGElements(objects, options);
            clearCommentImages();
            if (commentsToDisplay.length) {
              commentsToDisplay.forEach((comment: IComment) => {
                if (comment.x1 === null || comment.y1 == null) {
                  return;
                }
                const commentCoordinates = getIconPointForComment(comment);
                if (commentCoordinates) {
                  addCommentIcon(source, commentCoordinates.left, commentCoordinates.top, comment);
                }
              });
            }

            if (needsRecreatingZonesAndPopovers) {
              setTimeout(() => {
                setShouldDisplayAllComments(true);
                setTimeout(() => {
                  commentImages.current.forEach((commentImageData) => {
                    const comment = commentImageData.comment;
                    const image = commentImageData.image;

                    if (lastEditedCommentId.current === comment.commentId) return;

                    const rectExists = props.canvas
                      .getObjects()
                      .some(
                        (obj) =>
                          obj.name === COMMENTZONE_FOR_EDIT_OBJECT_NAME &&
                          obj.left === image.left &&
                          obj.top === image.top,
                      );

                    if (rectExists || commentGroups.some((item) => item.commentId === comment.commentId)) {
                      return;
                    }

                    addCommentGroup(image, comment);
                  });
                }, 10);
              }, 0);
            }
          },
        );
      } else clearCommentImages();
    } finally {
      if (needsRecreatingZonesAndPopovers && !props.commentsOnPageVisible) {
        setTimeout(() => {
          setShouldDisplayAllComments(true);
        }, 0);
      }
    }
  }

  useEffect(() => {
    recreateAllComments();
  }, [
    props.pageImages,
    commentsToDisplay,
    props.commentsOnPageVisible,
    props.orgzId,
    activeOnPageComment,
    commentGroups.length,
    isCommentInMove,
    editedCommentGroup,
    props.previewType,
    refreshComments,
  ]);

  useEffect(() => {
    setCommentsToDisplay(props.comments);
    setEditedCommentGroup(undefined);
  }, [props.comments]);

  useEffect(() => {
    if (!shouldDisplayAllComments) {
      if (commentsWithPopperCoords.length) {
        setCommentsWithPopperCoords([]);
      }
      if (closedCommentsIds.length) {
        setClosedCommentsIds([]);
      }
    }
  }, [shouldDisplayAllComments, commentsWithPopperCoords, closedCommentsIds, props.canvas]);

  useEffect(() => {
    const iconScale = getCurrentCommentIconScale(props.canvasZoom);
    commentImages.current.forEach((obj: commentImageData) => {
      obj.image.scale(iconScale);
    });
  }, [props.canvasZoom]);

  useEffect(() => {
    props.actionsToTrigger.forEach((action) => {
      switch (action.type) {
        case PreviewActionType.clearComments: {
          clearCommentImages();
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.rotateComments: {
          rotateCommentImages(action.payload as Coords);
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.moveCommentsPopovers: {
          updateCommentPopperPosition();
          props.onActionTrigger(action);
          break;
        }
      }
    });
  }, [props.actionsToTrigger]);

  const setCurrentCursor = useCallback(
    (forceDefault?: boolean) => {
      const defaultCursor = 'pointer';
      if (props.isNewCommentActive && !forceDefault) {
        props.canvas.defaultCursor = commentCursor.current;
        props.canvas.hoverCursor = commentCursor.current;
        props.canvas.setCursor(commentCursor.current);
        props.canvas.moveCursor = 'move';
      } else {
        props.canvas.defaultCursor = defaultCursor;
        props.canvas.hoverCursor = defaultCursor;
        props.canvas.setCursor(defaultCursor);
        props.canvas.moveCursor = 'move';
      }
    },
    [props.canvas, props.isNewCommentActive],
  );

  const setCursorFromSvg = useCallback(async () => {
    try {
      const svgData = await svgToPng(OnPageCommentIcon, 0, false);
      if (svgData) {
        const commentCursorString = `url(${svgData.dataUrl}) ${Math.round(svgData.width / 2)} ${
          svgData.height / 2
        }, auto`;
        commentCursor.current = commentCursorString;
        setCurrentCursor();
      }
    } catch {
      console.log('error creating cursor');
    }
  }, [setCurrentCursor]);

  const {
    handleCanvasMouseDown,
    handleCanvasMouseMove: commentZoneAddingMouseMove,
    handleCanvasMouseUp,
  } = useCommentZone({
    canvas: props.canvas,
    enabled: props.isNewCommentActive,
    getCanvasPoint: props.getCanvasPoint,
    onFinishDrawingZone: handleAddNewComment,
  });

  const handleCanvasMouseMove = useCallback(
    (event: fabric.IEvent<MouseEvent>) => {
      if (!isNewCommentActive) {
        return;
      }

      const canvasPoints = getCanvasPoint(event.e.x, event.e.y);
      const targetImageData: pageImageData | undefined = getTargetPageImage(pageImages, canvasPoints.x, canvasPoints.y);
      setCurrentCursor(!targetImageData);
      commentZoneAddingMouseMove(event);
    },
    [isNewCommentActive, getCanvasPoint, pageImages, setCurrentCursor, commentZoneAddingMouseMove],
  );

  useEffect(() => {
    setCursorFromSvg();
  }, [props.isNewCommentActive, setCursorFromSvg]);

  useEffect(() => {
    if (props.isNewCommentActive) {
      props.updateIsMouseScrollDisabled(true);
    } else props.updateIsMouseScrollDisabled(false);
  }, [props.isNewCommentActive]);

  useCanvasHandlers({
    canvas: props.canvas,
    enabled: props.isNewCommentActive,
    onMouseDown: handleCanvasMouseDown,
    onMouseMove: handleCanvasMouseMove,
    onMouseUp: handleCanvasMouseUp,
  });

  useEffect(() => {
    if (commentIdToShowZoneAfterRecreation.current) {
      const groupData = commentGroups.find((g) => g.commentId === commentIdToShowZoneAfterRecreation.current);
      if (groupData) {
        props.canvas.setActiveObject(groupData.group);
        const comment = (commentsToDisplay || props.comments).find((c) => c.commentId === groupData.commentId);
        if (comment) {
          openCommentPopover(groupData.group, comment);
        }
        commentIdToShowZoneAfterRecreation.current = null;
      }
    }
  }, [commentGroups, commentsToDisplay, props.comments]);

  return (
    <>
      {/* New comment popover */}
      {showNewCommentPopover &&
        activeOnPageComment &&
        props.orgzId &&
        newComment &&
        activeOnPageComment.comment.commentId === newComment.commentId && (
          <div style={{ zIndex: 1100, position: 'relative' }}>
            <CommentPopover
              orgzId={props.orgzId}
              comment={activeOnPageComment.comment}
              anchorCoords={{
                x: activeOnPageComment.x,
                y: activeOnPageComment.y,
                width: COMMENT_ICON_SIZE,
                height: COMMENT_ICON_SIZE,
              }}
              onClick={() => updateCommentsOrder(activeOnPageComment.comment.commentId)}
              onClose={handleCancelComment}
              onCancel={handleCancelComment}
              onSave={handleSaveComment}
              onRemove={handleRemoveComment}
              onRemoveZone={handleRemoveZone}
              onEditZone={handleEditZone}
              onAddZone={handleAddZone}
              mode="create"
              readOnly={!props.canEditComment}
              placement={getCommentPopperPlacement(activeOnPageComment)}
            />
          </div>
        )}

      {/* Existing comment popover (view mode) */}
      {!showNewCommentPopover && activeOnPageComment && props.orgzId && !newComment && (
        <div style={{ zIndex: 1000, position: 'relative' }}>
          <CommentPopover
            orgzId={props.orgzId}
            comment={activeOnPageComment.comment}
            anchorCoords={{
              x: activeOnPageComment.x,
              y: activeOnPageComment.y,
              width: COMMENT_ICON_SIZE,
              height: COMMENT_ICON_SIZE,
            }}
            onClick={() => updateCommentsOrder(activeOnPageComment.comment.commentId)}
            onClose={handleCancelComment}
            onCancel={handleCancelComment}
            onSave={handleSaveComment}
            onRemove={handleRemoveComment}
            onRemoveZone={handleRemoveZone}
            onEditZone={handleEditZone}
            onAddZone={handleAddZone}
            mode="view"
            readOnly={!props.canEditComment}
            placement={getCommentPopperPlacement(activeOnPageComment)}
          />
        </div>
      )}

      {/* All other popovers (unchanged) */}
      {props.commentsOnPageVisible &&
        commentsWithPopperCoords.map((item: commentImageDataWithPopperPosition) => {
          if (
            props.orgzId &&
            !closedCommentsIds.find((id: string) => id === item.comment.commentId) &&
            (!newComment || item.comment.commentId !== newComment.commentId)
          ) {
            return (
              <CommentPopover
                key={item.comment.commentId}
                orgzId={props.orgzId}
                comment={item.comment}
                anchorCoords={{
                  x: item.x,
                  y: item.y,
                  width: COMMENT_ICON_SIZE,
                  height: COMMENT_ICON_SIZE,
                }}
                onClick={() => updateCommentsOrder(item.comment.commentId)}
                onClose={() => handleCloseOneOfOpenedComment(item.comment.commentId)}
                onCancel={handleCancelComment}
                onSave={handleSaveComment}
                onRemove={handleRemoveComment}
                onRemoveZone={handleRemoveZone}
                onEditZone={handleEditZone}
                onAddZone={handleAddZone}
                mode="view"
                avoidAwayClosing={true}
                readOnly={!props.canEditComment}
                placement={getCommentPopperPlacement(item)}
              />
            );
          }
          return null;
        })}

      {commentOutsideRGZPromptVisible && (
        <Prompt
          type="error"
          mainActionButtonText="OK"
          open={true}
          mainText="Nie możesz przenieść komentarza, ponieważ komentarz lub jego przypisany obszar wychodzi poza skan pracy zdającego"
          onConfirm={() => setCommentOutsideRGZPromptVisible(false)}
        />
      )}
    </>
  );
};

export default CommentsOnPageManager;
